import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApproveProject } from '../../hooks/useProjects.js';
import { useToast } from '../ui/Toast.js';
import { Button } from '../ui/Button.js';
import { Textarea } from '../ui/Textarea.js';
import { Modal, MODAL_EXIT_MS } from '../ui/Modal.js';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ApproveProjectModalProps {
  projectId: number;
  revisionNumber: number;
  currentVersion: number;
  onClose: () => void;
  onApproved: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function ApproveProjectModal({
  projectId,
  revisionNumber,
  currentVersion,
  onClose,
  onApproved,
}: ApproveProjectModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const approve = useApproveProject(projectId);

  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => onClose(), MODAL_EXIT_MS + 20);
  }, [onClose]);

  function handleApprove() {
    setError(null);
    approve.mutate(
      note.trim() ? { note: note.trim() } : {},
      {
        onSuccess: (data) => {
          toast.success(t('projects.approve.successToast', { n: data.revisionNumber }));
          onApproved();
          handleClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : String(err);
          setError(msg);
        },
      },
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={t('projects.approve.modalTitle', { n: revisionNumber })}
      preventClose={approve.isPending}
      footer={
        <>
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            onClick={handleClose}
            disabled={approve.isPending}
          >
            {t('projects.approve.cancelButton')}
          </Button>
          <Button
            variant="solid"
            tone="cyan"
            size="sm"
            loading={approve.isPending}
            onClick={handleApprove}
          >
            {t('projects.approve.confirmButton', { n: revisionNumber })}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-5)' }}>
        {/* Note textarea */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          <label
            style={{
              fontSize: 'var(--evo-fs-label)',
              fontWeight: 'var(--evo-fw-medium)',
              color: 'var(--evo-fg-2)',
              fontFamily: 'var(--evo-font)',
            }}
          >
            {t('projects.approve.noteLabel')}
          </label>
          <Textarea
            value={note}
            onChange={setNote}
            placeholder={t('projects.approve.notePlaceholder')}
            maxLength={1000}
            rows={4}
            disabled={approve.isPending}
            resize="vertical"
            fullWidth
          />
        </div>

        {/* Consequence list */}
        <div
          style={{
            padding: 'var(--evo-space-3) var(--evo-space-4)',
            background: 'var(--evo-surface)',
            borderRadius: 'var(--evo-r-sm)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--evo-space-2)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--evo-fs-label)',
              fontWeight: 'var(--evo-fw-medium)',
              color: 'var(--evo-fg-3)',
              fontFamily: 'var(--evo-font)',
            }}
          >
            {t('projects.approve.consequencesTitle')}
          </span>
          {[
            t('projects.approve.consequenceVersion', { v: currentVersion, n: revisionNumber }),
            t('projects.approve.consequenceArchive'),
            t('projects.approve.consequenceStatus'),
          ].map((text) => (
            <div
              key={text}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)' }}
            >
              <span
                style={{
                  fontSize: 'var(--evo-fs-label)',
                  color: 'var(--evo-fg-3)',
                  fontFamily: 'var(--evo-font)',
                  flexShrink: 0,
                  lineHeight: '1.5',
                  userSelect: 'none',
                }}
              >
                ·
              </span>
              <span
                style={{
                  fontSize: 'var(--evo-fs-label)',
                  color: 'var(--evo-fg-2)',
                  fontFamily: 'var(--evo-font)',
                  lineHeight: '1.5',
                }}
              >
                {text}
              </span>
            </div>
          ))}
        </div>

        {/* Error message */}
        {error && (
          <div
            role="alert"
            style={{
              fontSize: 'var(--evo-fs-label)',
              color: 'var(--evo-error, #e53e3e)',
              fontFamily: 'var(--evo-font)',
              padding: 'var(--evo-space-2) var(--evo-space-3)',
              background: 'color-mix(in oklch, var(--evo-error, #e53e3e) 10%, transparent)',
              borderRadius: 'var(--evo-r-sm)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
